import pRetry from 'p-retry';
import { useAdditionalInfosResource } from '../use-additional-infos-event-resource';
import { createEventController } from './create-event-controller';
import { AdditionalInfosController } from './types';

export interface CreateScheduledEventControllerConfig {
    entityId: number;
    resource: ReturnType<typeof useAdditionalInfosResource>;
    pageSize?: number;
}

/**
 * Scheduled event controller.
 *
 * Overrides loadPage (different endpoint, always hasNextPage: false) and
 * createFilterDirective (() => true). Delegates createInfo, deleteInfo, and
 * createStreamListeners to the event controller — scheduled events use the
 * same CRUD APIs and the same 'infos' WebSocket stream.
 */
export function createScheduledEventController(
    config: CreateScheduledEventControllerConfig
): AdditionalInfosController {
    const { entityId, resource, pageSize } = config;

    const eventController = createEventController({
        entityId,
        resource,
        pageSize,
        includeEventNarratives: true,
        // Scheduled-event controller never calls the paginated endpoint, so the
        // flag is irrelevant — but the config requires it. Hardcode true to
        // avoid leaking the legacy code path's quirks into shared CRUD/stream
        // logic on the new endpoint code path.
        useNarrativeEndpoints: true,
    });

    return {
        async loadPage() {
            return pRetry(
                async () => {
                    const response = await resource.getScheduledEventInfos({
                        agencyEventId: entityId,
                    });
                    const data = response?.data?.data;
                    return {
                        additionalInfos: data?.additionalInfos ?? [],
                        pagination: {},
                        agencyEventNumberToIdMap: data?.agencyEventNumberToIdMap ?? {},
                    };
                },
                { retries: 2 }
            );
        },

        createInfo: eventController.createInfo,
        deleteInfo: eventController.deleteInfo,

        async editInfo({ additionalInfoId, text }) {
            const result = await resource.editInfo({
                additionalInfoId,
                isScheduledEvent: true,
                scheduledEventId: entityId,
                text,
            });
            const partial = result.data.data;
            if (!partial) {
                throw new Error(
                    `Edit scheduled event narrative returned no data for id=${additionalInfoId}`
                );
            }
            return { id: partial.additionalInfoId, text: partial.text, priority: partial.priority };
        },

        async setPriority({ additionalInfoId, priority }) {
            const result = await resource.setPriority({
                additionalInfoId,
                isScheduledEvent: true,
                scheduledEventId: entityId,
                priority,
            });
            const partial = result.data.data;
            if (!partial) {
                throw new Error(
                    `Emphasize scheduled event narrative returned no data for id=${additionalInfoId}`
                );
            }
            return { id: partial.additionalInfoId, text: partial.text, priority: partial.priority };
        },

        // Scheduled events support pin + emphasize (the full row-action set minus
        // delete/create) and share the event narrative/pin endpoints keyed on the
        // scheduled event id, so delegate straight to the event controller.
        getPinnedNarratives: eventController.getPinnedNarratives,
        pinNarrative: eventController.pinNarrative,
        unpinNarrative: eventController.unpinNarrative,

        createStreamListeners: eventController.createStreamListeners,

        createFilterDirective() {
            return () => true;
        },
    };
}
